import { useState } from "react";
import { submitEnquiry } from "@/services/enquiry";

interface EnquiryFormProps {
  /** Origin page tag written to the CRM lead (contact, investment-guide, projects, models, ...) */
  page: string;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  /** Optional interests shown as a dropdown (investment_type) */
  interestOptions?: string[];
  /** Optional secondary option mapped to revenue_model */
  modelOptions?: string[];
  /** Optional destination dropdown — selected value is prepended to the message */
  destinationOptions?: string[];
  ctaLabel?: string;
  /** Pre-fill the interest dropdown */
  defaultInterest?: string;
  /** Use the accent colour for the CTA button instead of primary */
  accent?: boolean;
  /** A specific property id to attach to the lead (optional) */
  propertyId?: number;
  className?: string;
}

export default function EnquiryForm({
  page,
  eyebrow,
  title = "Enquire Now",
  subtitle = "Share your details and our investment team will get back to you within 24 hours.",
  interestOptions,
  modelOptions,
  destinationOptions,
  ctaLabel = "Send Enquiry",
  defaultInterest,
  accent = false,
  propertyId,
  className = "",
}: EnquiryFormProps) {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("website_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      return;
    }

    const destination = String(formData.get("destination") || "").trim();
    let message = String(formData.get("message") || "").trim() || undefined;
    if (destination && destination !== "") {
      message = `[Preferred Destination: ${destination}] ${message || ""}`.trim();
    }

    const payload = {
      full_name: String(formData.get("name") || "").trim(),
      email: String(formData.get("email") || "").trim() || undefined,
      phone: String(formData.get("phone") || "").trim() || undefined,
      message,
      investment_type: String(formData.get("interest") || "") || undefined,
      revenue_model: String(formData.get("model") || "") || undefined,
      page,
      property_id: propertyId,
    };

    if (!payload.full_name) {
      setStatus("error");
      setFormError("Please enter your name.");
      return;
    }

    const result = await submitEnquiry(payload);
    if (result.ok) {
      setStatus("success");
      setFormError("");
      form.reset();
    } else {
      setStatus("error");
      setFormError(result.message);
    }
  }

  return (
    <div className={className}>
      {eyebrow && (
        <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-accent-600">
          <i className="ri-message-3-line"></i> {eyebrow}
        </span>
      )}
      <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-3">{title}</h3>
      <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{subtitle}</p>

      {status === "success" ? (
        <div className="mt-6 rounded-2xl bg-primary-50 border border-primary-200 p-6 text-center">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-100 text-primary-700">
            <i className="ri-check-line text-2xl"></i>
          </span>
          <p className="font-heading text-lg text-foreground-950 mt-3">Thank You!</p>
          <p className="text-sm text-foreground-600 mt-1.5 leading-relaxed">
            Your enquiry has been received. Our villa investment team will get back to you within
            24 hours.
          </p>
          <button
            onClick={() => setStatus("idle")}
            className="mt-4 text-sm font-semibold text-primary-700 hover:text-primary-800"
          >
            Send another enquiry
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} data-readdy-form className="mt-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor={`name-${page}`} className="block text-xs font-semibold text-foreground-800 mb-1.5">
                Full Name
              </label>
              <input
                id={`name-${page}`}
                name="name"
                type="text"
                required
                placeholder="Your name"
                className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label htmlFor={`phone-${page}`} className="block text-xs font-semibold text-foreground-800 mb-1.5">
                Phone / WhatsApp
              </label>
              <input
                id={`phone-${page}`}
                name="phone"
                type="tel"
                placeholder="+91 00000 00000"
                className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>

          <div>
            <label htmlFor={`email-${page}`} className="block text-xs font-semibold text-foreground-800 mb-1.5">
              Email Address
            </label>
            <input
              id={`email-${page}`}
              name="email"
              type="email"
              required
              placeholder="you@example.com"
              className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          {(interestOptions || modelOptions || destinationOptions) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {interestOptions && (
                <div>
                  <label htmlFor={`interest-${page}`} className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    I'm interested in
                  </label>
                  <select
                    id={`interest-${page}`}
                    name="interest"
                    defaultValue={defaultInterest || ""}
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="">Select an option</option>
                    {interestOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
              {modelOptions && (
                <div>
                  <label htmlFor={`model-${page}`} className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    Revenue model
                  </label>
                  <select
                    id={`model-${page}`}
                    name="model"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="">Select a model</option>
                    {modelOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
              {destinationOptions && (
                <div>
                  <label htmlFor={`destination-${page}`} className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    Preferred Destination
                  </label>
                  <select
                    id={`destination-${page}`}
                    name="destination"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="">Select a destination</option>
                    {destinationOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          <div>
            <label htmlFor={`message-${page}`} className="block text-xs font-semibold text-foreground-800 mb-1.5">
              Message <span className="text-foreground-500 font-normal">(optional)</span>
            </label>
            <textarea
              id={`message-${page}`}
              name="message"
              rows={3}
              maxLength={500}
              placeholder="Budget, timeline, preferred location..."
              className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
            ></textarea>
          </div>

          <input
            type="text"
            name="website_alt"
            className="hp-field"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          {status === "error" && formError && (
            <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">
              <i className="ri-error-warning-line mr-1.5"></i>
              {formError}
            </div>
          )}

          <button
            type="submit"
            className={`w-full inline-flex items-center justify-center gap-2 text-sm md:text-base font-semibold whitespace-nowrap rounded-full px-6 py-3.5 transition-colors ${
              accent
                ? "bg-accent-600 text-background-50 hover:bg-accent-700"
                : "bg-primary-600 text-background-50 hover:bg-primary-700"
            }`}
          >
            {ctaLabel}
            <i className="ri-arrow-right-line"></i>
          </button>
          <p className="text-center text-[11px] text-foreground-600">
            We respect your privacy. Your details are only used to respond to your enquiry.
          </p>
        </form>
      )}
    </div>
  );
}